import React from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';

interface HelpFAQModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function HelpFAQModal({ isOpen, onClose }: HelpFAQModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[600px] max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Help & Frequently Asked Questions</DialogTitle>
          <DialogDescription>
            Learn how to use VibeCraft and find answers to common questions.
          </DialogDescription>
        </DialogHeader>

        <Accordion type="single" collapsible className="w-full">
          <AccordionItem value="item-1">
            <AccordionTrigger>How do I play VibeCraft?</AccordionTrigger>
            <AccordionContent>
              <p className="mb-2">VibeCraft is a creative element combination game where you:</p>
              <ol className="list-decimal pl-5 space-y-2">
                <li>Combine elements in the machines to discover new elements</li>
                <li>Match elements with your goals to earn points</li>
                <li>Use skips to change goals that are too difficult</li>
                <li>Add your favorite elements to your favorites collection</li>
              </ol>
              <p className="mt-2">The more you discover, the more combinations become possible!</p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="item-2">
            <AccordionTrigger>How do I combine elements?</AccordionTrigger>
            <AccordionContent>
              <p>
                Drag elements from the Elements & Discoveries panel to the input slots in any of the
                three forges (Item, Location, or Character). Once you have two elements in a forge,
                click the "Combine" button to create a new element.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="item-2b">
            <AccordionTrigger>What are the different forge types?</AccordionTrigger>
            <AccordionContent>
              <p className="mb-2">
                VibeCraft has three different forge types, each creating different kinds of
                elements:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  <strong>Item Forge:</strong> Creates physical objects, tools, materials, and
                  tangible things
                </li>
                <li>
                  <strong>Location Forge:</strong> Creates places, environments, buildings, and
                  settings
                </li>
                <li>
                  <strong>Character Forge:</strong> Creates beings, creatures, people, and entities
                </li>
              </ul>
              <p className="mt-2">
                Try different combinations in each forge type to discover a wide variety of
                elements. Some combinations might work in one forge but create something completely
                different in another!
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="item-3">
            <AccordionTrigger>How do I complete goals?</AccordionTrigger>
            <AccordionContent>
              <p>
                Drag an element that matches one of your goals to the Goals panel. If the element
                name matches the goal word, you'll earn points and get a new goal.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="item-4">
            <AccordionTrigger>What are skips and how do I use them?</AccordionTrigger>
            <AccordionContent>
              <p>
                Skips allow you to change a goal that's too difficult. Click the Skip button, then
                click on the goal you want to skip. You'll get a new random goal. You earn skips by
                completing goals.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="item-5">
            <AccordionTrigger>Why do I need to report elements?</AccordionTrigger>
            <AccordionContent>
              <p>
                Since VibeCraft has endless possibilities for element combinations, occasionally
                inappropriate or incorrect elements might be created. If you find an element that's
                offensive, inappropriate, or just doesn't make sense, please report it so we can
                review it.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="item-6">
            <AccordionTrigger>How do actions work?</AccordionTrigger>
            <AccordionContent>
              <p>
                Actions are used whenever you combine elements or complete goals. You start with a
                limited number of actions, and you can get more by purchasing them. When you run out
                of actions, you'll need to get more to continue playing.
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="item-7">
            <AccordionTrigger>How do I add elements to favorites?</AccordionTrigger>
            <AccordionContent>
              <p className="mb-2">
                The Favorites panel helps you organize and quickly access your most useful elements:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>
                  Drag elements from the Elements & Discoveries panel to the Favorites panel to add
                  them
                </li>
                <li>Drag them back to the Elements & Discoveries panel to remove them</li>
                <li>Use favorites to keep your commonly used elements easily accessible</li>
                <li>Favorites are saved with your account and persist between sessions</li>
              </ul>
              <p className="mt-2">
                Pro tip: Add your most versatile elements to favorites to speed up your
                combinations!
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="item-8">
            <AccordionTrigger>What are keys and how does the Deconstructor work?</AccordionTrigger>
            <AccordionContent>
              <p className="mb-2">
                Keys and the Deconstructor allow you to break down elements into their component
                parts:
              </p>
              <ul className="list-disc pl-5 space-y-1">
                <li>Keys have a 5% chance to drop when you combine elements</li>
                <li>Each key allows you to use the Deconstructor once</li>
                <li>The Deconstructor breaks an element down into two simpler components</li>
                <li>
                  To use the Deconstructor, click on the lock icon (if you have keys) to unlock it
                </li>
                <li>Then drag an element to the input slot and click "Deconstruct"</li>
                <li>You'll receive two elements from which you can choose only one</li>
              </ul>
              <p className="mt-2">
                The Deconstructor is especially useful for discovering new base elements that can
                lead to many new combinations!
              </p>
            </AccordionContent>
          </AccordionItem>

          <AccordionItem value="item-9">
            <AccordionTrigger>What happens to my progress?</AccordionTrigger>
            <AccordionContent>
              <p>
                Your progress is automatically saved to your account. When you sign in again, all
                your elements, discoveries, goals, points, keys, and favorites will be there waiting
                for you.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>

        <DialogFooter>
          <Button onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
